import { useEffect, useState } from "react";
import { Button } from 'reactstrap';
import { db, functions } from '../firebase';
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, getDocs, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import Holidays from "date-holidays";
import moment from "moment";
import { toast } from 'react-toastify';
import { DEFAULT_RESERVATION_POOLS, DEFAULT_TOTAL_DAILY_CAPACITY, generateDailyTimeSlots, getPoolDailyLimit } from "../reservationPools";
import { warehouseDateKey } from "../warehouseTime";

function HolidaysPage() {
  const siteName = process.env.REACT_APP_SITE_NAME || "Inbound appointment Slam Hoyerswerda";
  const siteEnv = (process.env.REACT_APP_SITE_ENV || "").toLowerCase();
  const isOschatzSite = siteEnv === "oschatz" || siteName.includes("Oschatz");
  const defaultLooseSlotsPerMonth = isOschatzSite ? 1 : 2;
  const [savedHolidays, setSavedHolidays] = useState('');
  const [suggestedHolidays, setSuggestedHolidays] = useState('');
  const [numNormalSlots, setNumNormalSlots] = useState('6'); // Liczba slotów (4-16)
  const [looseSlotsPerMonth, setLooseSlotsPerMonth] = useState(defaultLooseSlotsPerMonth.toString()); // 1-8 na miesiąc
  const [maxSlotsPerDay, setMaxSlotsPerDay] = useState('12'); // Max rezerwacji na dzień
  const [reservationPools, setReservationPools] = useState(DEFAULT_RESERVATION_POOLS);
  const [totalDailyCapacity, setTotalDailyCapacity] = useState(String(DEFAULT_TOTAL_DAILY_CAPACITY));
  const [poolSaveState, setPoolSaveState] = useState('');
  const [poolCodeInputs, setPoolCodeInputs] = useState({});
  const [activeSettingsTab, setActiveSettingsTab] = useState('holidays');
  const navigate = useNavigate();

  const holidaysFromDB = async () => {
    try {
      const docRef = doc(db, 'calendar', 'i5UKDRTsOKiBbTcUWiN6');
      const response = await getDoc(docRef);
      if (response.exists() && response.data().holidays) {
        const holidayArray = response.data().holidays.map(holiday => 
          moment(new Date(holiday.seconds * 1000)).format('YYYY-MM-DD')
        );
        setSavedHolidays(holidayArray.join(','));
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  };

  useEffect(() => {
    const hd = new Holidays("DE");
    const currentYear = new Date().getFullYear();
    const holidaysThisYear = hd.getHolidays(currentYear);
    const holidaysNextYear = hd.getHolidays(currentYear + 1);
    const allHolidays = [...holidaysThisYear, ...holidaysNextYear];
    const suggestedDates = allHolidays
      .filter(holiday => holiday.type === 'public')
      .map(holiday => holiday.date.split(' ')[0])
      .join(',');
    setSuggestedHolidays(suggestedDates);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        if (user.displayName === 'porter') navigate('/super-admin/parking-map');
        else if (user.displayName === 'manager') navigate('/super-admin/pre-alerts-managment');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    holidaysFromDB();
    loadSlotsConfig(); // Załaduj ustawienia slotów z bazy
  }, []);

  const loadSlotsConfig = async () => {
    try {
      const slotsConfigRef = doc(db, 'calendar', 'slotsConfig');
      const docSnap = await getDoc(slotsConfigRef);
      if (docSnap.exists()) {
        const config = docSnap.data();
        if (config.numNormalSlots !== undefined) setNumNormalSlots(config.numNormalSlots.toString());
        if (config.looseSlotsPerMonth !== undefined) setLooseSlotsPerMonth(config.looseSlotsPerMonth.toString());
        if (config.maxSlotsPerDay !== undefined) setMaxSlotsPerDay(config.maxSlotsPerDay.toString());
        if (Array.isArray(config.reservationPools)) setReservationPools(config.reservationPools);
        if (config.totalDailyCapacity !== undefined) setTotalDailyCapacity(String(config.totalDailyCapacity));
      }
    } catch (error) {
      console.error("Error loading slots config:", error);
    }
  };

  const areReservationPoolsValid = (pools, totalCapacity) => {
    const codes = pools.flatMap((pool) => (pool.codes || []).map((code) => String(code).trim().toUpperCase()));
    const total = Number(totalCapacity);
    if (!Number.isInteger(total) || total < 0 || total > 16 || !pools.length || codes.some((code) => !code) || new Set(codes).size !== codes.length) return false;
    if (pools.reduce((sum, pool) => sum + Number(pool.maxPerDay), 0) !== total) return false;

    const poolsAreValid = pools.every((pool) => {
      if (!pool.name.trim() || !Number.isInteger(Number(pool.maxPerDay)) || Number(pool.maxPerDay) < 0 || !Number.isInteger(Number(pool.looseSlotsPerMonth)) || Number(pool.looseSlotsPerMonth) < 0) return false;
      const ranges = [...(pool.capacityRanges || [])].sort((a, b) => a.startDate.localeCompare(b.startDate));
      if (ranges.some((range) => !/^\d{4}-\d{2}-\d{2}$/.test(range.startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(range.endDate) || range.startDate > range.endDate || !Number.isInteger(Number(range.maxPerDay)) || Number(range.maxPerDay) < 0)) return false;
      return ranges.every((range, index) => index === 0 || ranges[index - 1].endDate < range.startDate);
    });
    if (!poolsAreValid) return false;

    const rangeBoundaryDates = new Set(pools.flatMap((pool) => (pool.capacityRanges || []).flatMap((range) => {
      const dayAfterEnd = new Date(`${range.endDate}T00:00:00.000Z`);
      dayAfterEnd.setUTCDate(dayAfterEnd.getUTCDate() + 1);
      return [range.startDate, range.endDate, dayAfterEnd.toISOString().slice(0, 10)];
    })));
    return [...rangeBoundaryDates].every((date) =>
      pools.reduce((sum, pool) => sum + getPoolDailyLimit(pool, date), 0) <= total
    );
  };

  // Funkcja do generowania godzin automatycznie między 8:00 a 16:00
  const generateTimeSlots = (numSlots) => {
    const numSlotsInt = parseInt(numSlots) || 6;
    return generateDailyTimeSlots(numSlotsInt);
  };

  const handleUpdate = async () => {
    try {
      const numSlots = parseInt(numNormalSlots);
      if (isNaN(numSlots) || numSlots < 1 || numSlots > 16) {
        toast.error("Number of slots must be between 1 and 16");
        return;
      }

      const slotStrings = generateTimeSlots(numSlots);
      const slotsConfigRef = doc(db, 'calendar', 'slotsConfig');
      const config = {
        normalSlots: slotStrings,
        numNormalSlots: numSlots,
        updatedAt: new Date(),
      }

      if (isOschatzSite) {
        const looseNum = parseInt(looseSlotsPerMonth);
        const maxSlotsNum = parseInt(maxSlotsPerDay);
        if (isNaN(looseNum) || looseNum < 0 || looseNum > 8 || isNaN(maxSlotsNum) || maxSlotsNum < 1 || maxSlotsNum > 20) {
          toast.error("Check the loose and daily reservation limits.");
          return;
        }
        config.looseSlotsPerMonth = looseNum;
        config.maxSlotsPerDay = maxSlotsNum;
      }

      await setDoc(slotsConfigRef, config, { merge: true });
      toast.success("Calendar slot settings saved.");
    } catch (err) {
      console.error("Error updating slot settings:", err);
      toast.error("Error saving slot settings. Please try again.");
    }
  };

  const handleHolidayUpdate = async () => {
    try {
      const holidayDates = savedHolidays.split(',').map((date) => date.trim()).filter(Boolean);
      for (const date of holidayDates) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !moment(date, 'YYYY-MM-DD', true).isValid()) {
          toast.error(`Invalid holiday date: "${date}". Use YYYY-MM-DD.`);
          return;
        }
      }

      const alertsRef = db.collection('pre-alerts_MIGRATED', 'euoso0pRR5PZw2wBnjOW', 'alerts');
      const affectedAlerts = await getDocs(alertsRef);
      const affectedAlertIds = affectedAlerts.docs
        .filter((alertDoc) => alertDoc.data().isCancel !== true)
        .filter((alertDoc) => {
          const alertDate = warehouseDateKey(alertDoc.data().firstDate);
          return alertDate && holidayDates.includes(alertDate);
        })
        .map((alertDoc) => `#${alertDoc.data().id || alertDoc.id}`);

      if (affectedAlertIds.length > 0) {
        const confirmation = window.confirm(
          `WARNING: The following pre-alerts are scheduled for the selected day(s): ${affectedAlertIds.join(', ')}\n\nAre you sure you want to add this holiday and automatically cancel these bookings?`
        );
        if (!confirmation) return;
      }

      const updateHolidaySettings = httpsCallable(functions, 'updateHolidaySettings');
      const result = await updateHolidaySettings({ holidayDates });
      toast.success(`Holiday settings saved. ${result.data.canceledCount || 0} booking(s) canceled.`);
    } catch (err) {
      console.error("Error updating holidays:", err);
      toast.error("Error saving holidays. Please try again.");
    }
  };

  const saveReservationPools = async () => {
    const normalizedPools = reservationPools.map((pool) => ({
      ...pool,
      name: pool.name.trim(),
      codes: (pool.codes || []).map((code) => String(code).trim().toUpperCase()).filter(Boolean),
      maxPerDay: Number(pool.maxPerDay),
      capacityRanges: (pool.capacityRanges || []).map((range) => ({
        ...range,
        maxPerDay: Number(range.maxPerDay),
      })),
      looseSlotsPerMonth: Number(pool.looseSlotsPerMonth),
    }));

    if (isOschatzSite) return;
    if (!areReservationPoolsValid(normalizedPools, totalDailyCapacity)) {
      toast.error("Pool allocations must add up to total daily capacity, and date ranges must stay within that total.");
      return;
    }

    try {
      setPoolSaveState('Saving...');
      await setDoc(doc(db, 'calendar', 'slotsConfig'), {
        reservationPools: normalizedPools,
        totalDailyCapacity: Number(totalDailyCapacity),
        normalSlots: generateDailyTimeSlots(totalDailyCapacity),
        numNormalSlots: Number(totalDailyCapacity),
        updatedAt: new Date(),
      }, { merge: true });
      setReservationPools(normalizedPools);
      setPoolSaveState('Saved');
      toast.success('Booking pools saved.');
    } catch (error) {
      console.error('Error saving booking pools:', error);
      setPoolSaveState('');
      toast.error('Could not save booking pools.');
    }
  };

  const updatePool = (poolIndex, field, value) => {
    setReservationPools((pools) => pools.map((pool, index) => {
      if (index !== poolIndex) return pool;
      if (field === 'codes') {
        const codes = Array.isArray(value) ? value : value.split(',');
        return { ...pool, codes: codes.map((code) => code.trim().toUpperCase()).filter(Boolean) };
      }
      return { ...pool, [field]: value };
    }));
    setPoolSaveState('');
  };

  const updateCapacityRange = (poolIndex, rangeIndex, field, value) => {
    setReservationPools((pools) => pools.map((pool, index) => index !== poolIndex ? pool : {
      ...pool,
      capacityRanges: (pool.capacityRanges || []).map((range, index) => index !== rangeIndex ? range : { ...range, [field]: value }),
    }));
    setPoolSaveState('');
  };

  return (
    <>
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />
      <div className="content" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <div role="tablist" aria-label="Holiday, calendar, and booking pool settings" style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid #ddd', flexWrap: 'wrap' }}>
            <button type="button" role="tab" aria-selected={activeSettingsTab === 'holidays'} onClick={() => setActiveSettingsTab('holidays')} style={{ padding: '0.75rem 1rem', border: 0, borderBottom: activeSettingsTab === 'holidays' ? '3px solid #2f855a' : '3px solid transparent', background: 'transparent', fontWeight: activeSettingsTab === 'holidays' ? 700 : 400 }}>
              Holidays
            </button>
            {isOschatzSite && <button type="button" role="tab" aria-selected={activeSettingsTab === 'calendar'} onClick={() => setActiveSettingsTab('calendar')} style={{ padding: '0.75rem 1rem', border: 0, borderBottom: activeSettingsTab === 'calendar' ? '3px solid #2f855a' : '3px solid transparent', background: 'transparent', fontWeight: activeSettingsTab === 'calendar' ? 700 : 400 }}>
              Calendar &amp; time slots
            </button>}
            {!isOschatzSite && <button type="button" role="tab" aria-selected={activeSettingsTab === 'pools'} onClick={() => setActiveSettingsTab('pools')} style={{ padding: '0.75rem 1rem', border: 0, borderBottom: activeSettingsTab === 'pools' ? '3px solid #2f855a' : '3px solid transparent', background: 'transparent', fontWeight: activeSettingsTab === 'pools' ? 700 : 400 }}>
              Destination pools
            </button>}
        </div>
        {activeSettingsTab === 'holidays' && (
          <>
        <div>
          <h5>Suggested German holidays (current and next year)</h5>
          <p>You can copy these dates and paste them into the field below.</p>
          <textarea 
            value={suggestedHolidays} 
            readOnly 
            style={{ width: '60%', height: '150px', padding: '10px', border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#f0f0f0' }}
          ></textarea>
        </div>
        
        <div>
          <h5>Active holidays (saved in the database)</h5>
          <p>Please enter vacation dates in the following format: 2024-12-28,2024-12-29.</p>
          <textarea 
            value={savedHolidays} 
            onChange={(e) => setSavedHolidays(e.target.value)} 
            style={{ width: '60%', height: '150px', padding: '10px', border: '1px solid #a6a6a6', borderRadius: '8px' }}
          ></textarea>
        </div>
        <Button onClick={handleHolidayUpdate} className="btn btn-warning" style={{ width: '160px' }}>Save holidays</Button>
          </>
        )}
        {isOschatzSite && activeSettingsTab === 'calendar' && (
          <>
        <div style={{ borderTop: '2px solid #ddd', paddingTop: '2rem' }}>
          <h5>Available Slots Configuration (A. Slots)</h5>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="numNormalSlots" style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
              Available appointment times per day
            </label>
            <input 
              id="numNormalSlots"
              type="number"
              min="1"
              max="16"
              value={numNormalSlots}
              onChange={(e) => setNumNormalSlots(e.target.value)}
              style={{ width: '60%', padding: '8px', border: '1px solid #a6a6a6', borderRadius: '4px' }}
            />
            <small style={{ display: 'block', marginTop: '0.3rem', color: '#666' }}>
              Set between 1 and 16. These time choices are shared across all destination pools. Daily booking capacity is configured under Destination pools.
            </small>
            {numNormalSlots && (
              <div style={{ marginTop: '0.5rem', padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                <strong>Generated times:</strong> {generateTimeSlots(numNormalSlots).join(', ')}
              </div>
            )}
          </div>

          {isOschatzSite && <>
          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="looseSlots" style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
              Number of Slots per Month for "Loose" Type
            </label>
            <input 
              id="looseSlots"
              type="number"
              min="0"
              max="8"
              value={looseSlotsPerMonth}
              onChange={(e) => setLooseSlotsPerMonth(e.target.value)}
              style={{ width: '60%', padding: '8px', border: '1px solid #a6a6a6', borderRadius: '4px' }}
            />
            <small style={{ display: 'block', marginTop: '0.3rem', color: '#666' }}>
              Set between 0 and 8 per month (0 disables "Loose" type). Slots are randomly distributed across weekdays (Mon-Fri).
            </small>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="maxSlotsPerDay" style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
              Max Slots Per Day (Reservations Limit)
            </label>
            <input 
              id="maxSlotsPerDay"
              type="number"
              min="1"
              max="20"
              value={maxSlotsPerDay}
              onChange={(e) => setMaxSlotsPerDay(e.target.value)}
              style={{ width: '60%', padding: '8px', border: '1px solid #a6a6a6', borderRadius: '4px' }}
            />
            <small style={{ display: 'block', marginTop: '0.3rem', color: '#666' }}>
              Maximum number of bookings allowed per day. When reached, the day is closed.
            </small>
          </div></>}
        </div>
        <Button onClick={handleUpdate} className="btn btn-warning" style={{ width: '180px' }}>
          Save calendar settings
        </Button>
          </>
        )}

        {!isOschatzSite && activeSettingsTab === 'pools' && (
          <div style={{ borderTop: '2px solid #ddd', paddingTop: '2rem' }}>
            <h5>Appointment destination pools</h5>
            <div style={{ maxWidth: '480px', marginBottom: '1.5rem' }}>
              <label htmlFor="totalDailyCapacity" style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
                Total daily capacity across all pools
              </label>
              <input id="totalDailyCapacity" className="form-control" type="number" min="0" max="16" step="1" value={totalDailyCapacity} onChange={(event) => {
                setTotalDailyCapacity(event.target.value);
                setPoolSaveState('');
              }} />
              <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#f4f6f5', borderRadius: '4px' }}>
                <strong>Generated appointment times:</strong> {generateDailyTimeSlots(totalDailyCapacity).join(', ') || 'No times while capacity is 0.'}
              </div>
              <small style={{ display: 'block', marginTop: '0.35rem', color: '#666' }}>
                Set from 0 to 16. The total capacity also sets the number of available appointment times between 08:00 and 16:00.
              </small>
              <small style={{ display: 'block', marginTop: '0.35rem', color: Number(totalDailyCapacity) === reservationPools.reduce((sum, pool) => sum + Number(pool.maxPerDay || 0), 0) ? '#2f855a' : '#b45309' }}>
                {(() => {
                  const remaining = Number(totalDailyCapacity) - reservationPools.reduce((sum, pool) => sum + Number(pool.maxPerDay || 0), 0);
                  return remaining === 0 ? 'All capacity is allocated.' : remaining > 0 ? `${remaining} slot(s) still need to be allocated.` : `${Math.abs(remaining)} slot(s) over the total capacity.`;
                })()}
              </small>
            </div>
            {reservationPools.map((pool, poolIndex) => (
              <div key={pool.id} style={{ border: '1px solid #ccc', padding: '1rem', marginBottom: '1rem', borderRadius: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center' }}>
                  <strong>{pool.name || `Pool ${poolIndex + 1}`}</strong>
                  <Button color="danger" size="sm" disabled={reservationPools.length <= 1} onClick={() => {
                    setReservationPools((pools) => pools.filter((_, index) => index !== poolIndex));
                    setPoolSaveState('');
                  }}>Remove pool</Button>
                </div>
                <label style={{ display: 'block', marginTop: '0.75rem' }}>
                  Pool name
                  <input className="form-control" value={pool.name} onChange={(event) => updatePool(poolIndex, 'name', event.target.value)} />
                </label>
                <div style={{ marginTop: '0.75rem' }}>
                  <strong>Destination codes</strong>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0.5rem 0' }}>
                    {(pool.codes || []).map((code) => (
                      <span key={code} style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', border: '1px solid #ccc', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                        {code}
                        <button type="button" aria-label={`Remove ${code}`} onClick={() => updatePool(poolIndex, 'codes', pool.codes.filter((entry) => entry !== code))}>×</button>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', maxWidth: '420px' }}>
                    <input className="form-control" aria-label="New destination code" value={poolCodeInputs[pool.id] || ''} onChange={(event) => setPoolCodeInputs((inputs) => ({ ...inputs, [pool.id]: event.target.value }))} onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        const code = (poolCodeInputs[pool.id] || '').trim().toUpperCase();
                        if (code) updatePool(poolIndex, 'codes', [...(pool.codes || []), code]);
                        setPoolCodeInputs((inputs) => ({ ...inputs, [pool.id]: '' }));
                      }
                    }} />
                    <Button type="button" color="secondary" onClick={() => {
                      const code = (poolCodeInputs[pool.id] || '').trim().toUpperCase();
                      if (!code) return;
                      updatePool(poolIndex, 'codes', [...(pool.codes || []), code]);
                      setPoolCodeInputs((inputs) => ({ ...inputs, [pool.id]: '' }));
                    }}>Add code</Button>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                  <label>
                    Daily allocation
                    <input className="form-control" type="number" min="0" value={pool.maxPerDay} onChange={(event) => updatePool(poolIndex, 'maxPerDay', event.target.value)} />
                  </label>
                  <label>
                    Loose limit per month
                    <input className="form-control" type="number" min="0" value={pool.looseSlotsPerMonth} onChange={(event) => updatePool(poolIndex, 'looseSlotsPerMonth', event.target.value)} />
                  </label>
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <strong>Daily limit date ranges</strong>
                  {(pool.capacityRanges || []).map((range, rangeIndex) => (
                    <div key={range.id || rangeIndex} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'end', marginTop: '0.5rem' }}>
                      <label>
                        From
                        <input className="form-control" type="date" value={range.startDate} onChange={(event) => updateCapacityRange(poolIndex, rangeIndex, 'startDate', event.target.value)} />
                      </label>
                      <label>
                        To
                        <input className="form-control" type="date" value={range.endDate} onChange={(event) => updateCapacityRange(poolIndex, rangeIndex, 'endDate', event.target.value)} />
                      </label>
                      <label>
                        Daily limit in range
                        <input className="form-control" type="number" min="0" value={range.maxPerDay} onChange={(event) => updateCapacityRange(poolIndex, rangeIndex, 'maxPerDay', event.target.value)} />
                      </label>
                      <Button color="danger" size="sm" onClick={() => updatePool(poolIndex, 'capacityRanges', (pool.capacityRanges || []).filter((_, index) => index !== rangeIndex))}>Remove range</Button>
                    </div>
                  ))}
                  <Button color="secondary" size="sm" style={{ marginTop: '0.5rem' }} onClick={() => updatePool(poolIndex, 'capacityRanges', [...(pool.capacityRanges || []), { id: `range-${Date.now()}`, startDate: '', endDate: '', maxPerDay: pool.maxPerDay }])}>Add date range</Button>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <Button color="secondary" onClick={() => {
                const id = `pool-${Date.now()}`;
                setReservationPools((pools) => [...pools, { id, name: 'New pool', codes: [], maxPerDay: 0, capacityRanges: [], looseSlotsPerMonth: 0 }]);
                setPoolSaveState('');
              }}>Add pool</Button>
              <Button color="primary" onClick={saveReservationPools}>Save pools</Button>
              <span role="status">{poolSaveState}</span>
            </div>
          </div>
        )}

      </div>
    </>
  );
}

export default HolidaysPage;